import React, { useMemo, useState } from "react";
import { useColors } from "../context/ThemeContext";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const pad = (n) => String(n).padStart(2, "0");

/** Parse `YYYY-MM-DDTHH:mm` or ISO → Date (local). */
export function parseLocalDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format Date → `YYYY-MM-DDTHH:mm` for form state / API local parse. */
export function toLocalDateTimeValue(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function formatDateTimeLabel(value) {
  const d = parseLocalDateTime(value);
  if (!d) return "Pick date & time";
  return d.toLocaleString("en-NG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function resolveMin(minDateTime) {
  if (!minDateTime) return null;
  if (minDateTime instanceof Date) {
    return Number.isNaN(minDateTime.getTime()) ? null : minDateTime;
  }
  return parseLocalDateTime(minDateTime);
}

/** Next 5-minute slot strictly after `min` (exclusive). */
function nextValidAfter(min) {
  const next = new Date(min.getTime() + 60 * 1000);
  const snapped = Math.ceil(next.getMinutes() / 5) * 5;
  next.setSeconds(0, 0);
  if (snapped === 60) {
    next.setHours(next.getHours() + 1, 0, 0, 0);
  } else {
    next.setMinutes(snapped, 0, 0);
  }
  if (next.getTime() <= min.getTime()) {
    next.setMinutes(next.getMinutes() + 5, 0, 0);
  }
  return next;
}

function clampToMin(date, min) {
  if (!min) return date;
  return date.getTime() <= min.getTime() ? nextValidAfter(min) : date;
}

/** Monday-first calendar cells for a month. */
function buildMonthCells(viewMonth) {
  const first = startOfMonth(viewMonth);
  const startOffset = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    const d = new Date(first);
    d.setDate(d.getDate() - (startOffset - i));
    cells.push({ date: d, inMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ date: new Date(first.getFullYear(), first.getMonth(), day), inMonth: true });
  }
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last);
    d.setDate(d.getDate() + 1);
    cells.push({ date: d, inMonth: false });
  }
  return cells;
}

const sameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Themed date + time picker matching PendPay dark/light palette.
 * value: `YYYY-MM-DDTHH:mm` string
 * minDateTime: Date | string — exclusive minimum (must pick strictly after)
 */
export default function EventDateTimePicker({
  value,
  onChange,
  label = "Starts at",
  minDateTime = null,
}) {
  const C = useColors();
  const min = resolveMin(minDateTime);
  const selected = clampToMin(parseLocalDateTime(value) || new Date(), min);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected));
  const [draft, setDraft] = useState(selected);

  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

  const isDayDisabled = (date) => {
    if (!min) return false;
    return startOfDay(date).getTime() < startOfDay(min).getTime();
  };

  const isHourDisabled = (h) => {
    if (!min) return false;
    if (startOfDay(draft).getTime() < startOfDay(min).getTime()) return true;
    if (!sameDay(draft, min)) return false;
    const lastMinute = new Date(draft);
    lastMinute.setHours(h, 55, 0, 0);
    return lastMinute.getTime() <= min.getTime();
  };

  const isMinuteDisabled = (m) => {
    if (!min) return false;
    const candidate = new Date(draft);
    candidate.setMinutes(m, 0, 0);
    return candidate.getTime() <= min.getTime();
  };

  const draftValid = !min || draft.getTime() > min.getTime();
  const todayDisabled = min ? isDayDisabled(new Date()) : false;
  const canGoPrevMonth =
    !min ||
    viewMonth.getFullYear() > min.getFullYear() ||
    (viewMonth.getFullYear() === min.getFullYear() && viewMonth.getMonth() > min.getMonth());

  const openPicker = () => {
    const base = clampToMin(parseLocalDateTime(value) || new Date(), min);
    const snapped = new Date(base);
    snapped.setMinutes(Math.round(snapped.getMinutes() / 5) * 5, 0, 0);
    const next = clampToMin(snapped, min);
    setDraft(next);
    setViewMonth(startOfMonth(next));
    setOpen(true);
  };

  const applyDay = (date) => {
    if (isDayDisabled(date)) return;
    const next = new Date(draft);
    next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    setDraft(clampToMin(next, min));
  };

  const applyHour = (h) => {
    if (isHourDisabled(h)) return;
    const next = new Date(draft);
    next.setHours(h);
    setDraft(clampToMin(next, min));
  };

  const applyMinute = (m) => {
    if (isMinuteDisabled(m)) return;
    const next = new Date(draft);
    next.setMinutes(m, 0, 0);
    setDraft(clampToMin(next, min));
  };

  const confirm = () => {
    if (!draftValid) return;
    onChange(toLocalDateTimeValue(draft));
    setOpen(false);
  };

  const setToday = () => {
    if (todayDisabled) return;
    const now = new Date();
    now.setMinutes(Math.round(now.getMinutes() / 5) * 5, 0, 0);
    const next = clampToMin(now, min);
    setDraft(next);
    setViewMonth(startOfMonth(next));
  };

  const clear = () => {
    onChange("");
    setOpen(false);
  };

  const monthLabel = viewMonth.toLocaleString("en-NG", { month: "long", year: "numeric" });

  return (
    <div style={{ marginBottom: 12 }}>
      {label ? (
        <label style={{ display: "block", fontSize: 12, color: C.muted, marginBottom: 6 }}>{label}</label>
      ) : null}
      <button
        type="button"
        onClick={openPicker}
        style={{
          width: "100%",
          textAlign: "left",
          padding: "14px 16px",
          borderRadius: 14,
          border: `1px solid ${C.border}`,
          background: C.card,
          color: value ? C.text : C.muted,
          fontSize: 15,
          fontWeight: value ? 700 : 500,
          cursor: "pointer",
          boxSizing: "border-box",
        }}
      >
        {formatDateTimeLabel(value)}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 4000,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 400,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 20,
              padding: 16,
              boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <button
                type="button"
                disabled={!canGoPrevMonth}
                onClick={() => {
                  if (!canGoPrevMonth) return;
                  setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
                }}
                style={{
                  ...navBtn(C),
                  opacity: canGoPrevMonth ? 1 : 0.35,
                  cursor: canGoPrevMonth ? "pointer" : "not-allowed",
                }}
                aria-label="Previous month"
              >
                ‹
              </button>
              <span style={{ color: C.text, fontWeight: 800, fontSize: 15 }}>{monthLabel}</span>
              <button
                type="button"
                onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
                style={navBtn(C)}
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 8 }}>
              {WEEKDAYS.map((d, i) => (
                <div key={`${d}-${i}`} style={{ textAlign: "center", color: C.muted, fontSize: 11, fontWeight: 700, padding: "4px 0" }}>
                  {d}
                </div>
              ))}
              {cells.map(({ date, inMonth }, i) => {
                const disabled = isDayDisabled(date);
                const selectedDay = !disabled && sameDay(date, draft);
                const isToday = sameDay(date, new Date());
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => applyDay(date)}
                    style={{
                      aspectRatio: "1",
                      borderRadius: 10,
                      border: selectedDay
                        ? `1.5px solid ${C.lime}`
                        : isToday && !disabled
                          ? `1px solid ${C.borderBright}`
                          : "1px solid transparent",
                      background: selectedDay ? C.limeDim : "transparent",
                      color: disabled ? C.muted : selectedDay ? C.lime : inMonth ? C.text : C.muted,
                      fontWeight: selectedDay ? 800 : 600,
                      fontSize: 13,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.28 : inMonth ? 1 : 0.45,
                      textDecoration: disabled ? "line-through" : "none",
                    }}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            <p style={{ color: C.muted, fontSize: 12, fontWeight: 700, margin: "12px 0 8px" }}>Time</p>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <div style={scrollCol(C)}>
                {hours.map((h) => {
                  const disabled = isHourDisabled(h);
                  return (
                    <button
                      key={h}
                      type="button"
                      disabled={disabled}
                      onClick={() => applyHour(h)}
                      style={{
                        ...timeChip(C, !disabled && draft.getHours() === h),
                        opacity: disabled ? 0.28 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                        textDecoration: disabled ? "line-through" : "none",
                      }}
                    >
                      {pad(h)}
                    </button>
                  );
                })}
              </div>
              <div style={scrollCol(C)}>
                {minutes.map((m) => {
                  const disabled = isMinuteDisabled(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      disabled={disabled}
                      onClick={() => applyMinute(m)}
                      style={{
                        ...timeChip(C, !disabled && draft.getMinutes() === m),
                        opacity: disabled ? 0.28 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                        textDecoration: disabled ? "line-through" : "none",
                      }}
                    >
                      {pad(m)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <button type="button" onClick={clear} style={ghostBtn(C)}>Clear</button>
              <button
                type="button"
                disabled={todayDisabled}
                onClick={setToday}
                style={{
                  ...ghostBtn(C),
                  opacity: todayDisabled ? 0.35 : 1,
                  cursor: todayDisabled ? "not-allowed" : "pointer",
                }}
              >
                Today
              </button>
            </div>
            <button
              type="button"
              disabled={!draftValid}
              onClick={confirm}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: 14,
                border: "none",
                background: C.lime,
                color: C.bg,
                fontWeight: 800,
                fontSize: 15,
                cursor: draftValid ? "pointer" : "not-allowed",
                opacity: draftValid ? 1 : 0.45,
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function navBtn(C) {
  return {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.card,
    color: C.text,
    fontSize: 20,
    cursor: "pointer",
    lineHeight: 1,
  };
}

function scrollCol(C) {
  return {
    flex: 1,
    maxHeight: 140,
    overflowY: "auto",
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: C.card,
    padding: 6,
  };
}

function timeChip(C, active) {
  return {
    width: "100%",
    padding: "10px 0",
    marginBottom: 4,
    borderRadius: 10,
    border: active ? `1.5px solid ${C.lime}` : "1px solid transparent",
    background: active ? C.limeDim : "transparent",
    color: active ? C.lime : C.text,
    fontWeight: active ? 800 : 600,
    fontSize: 14,
    cursor: "pointer",
  };
}

function ghostBtn(C) {
  return {
    flex: 1,
    padding: "12px",
    borderRadius: 12,
    border: `1px solid ${C.borderBright}`,
    background: "transparent",
    color: C.lime,
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  };
}
